import { useTranslation } from "react-i18next";
import { Phone, PhoneOff, Video } from "lucide-react";
import { avatarSrc } from "../../lib/api";

// Full-screen FaceTime-style ring screen. Used for BOTH an incoming call
// (Accept + Decline) and an outgoing call (Cancel only). Mobile-first.
export default function IncomingCall({ mode, person, onAccept, onDecline, onCancel }) {
  const { t } = useTranslation();
  const incoming = mode === "incoming";
  const name = person?.real_name || person?.display_name || (incoming ? t("call.someone") : t("call.connecting"));
  const photo = person ? avatarSrc(person) : null;

  return (
    <div
      className="fixed inset-0 z-[130] flex flex-col items-center justify-between overflow-hidden"
      data-testid={incoming ? "incoming-call-screen" : "outgoing-call-screen"}
      style={{ background: "radial-gradient(120% 100% at 50% 0%, #2a0000 0%, #0a0000 55%, #000 100%)" }}
    >
      {/* Ambient pulse */}
      <div className="pointer-events-none absolute top-[-20%] left-1/2 -translate-x-1/2 w-[140vw] h-[70vh] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(230,0,0,0.35) 0%, transparent 60%)" }} />

      <div className="relative flex flex-col items-center pt-[18vh] px-6 text-center">
        <p className="font-mono-accent text-[#FF6B6B] tracking-[0.3em] text-xs uppercase flex items-center gap-2" data-testid="call-label">
          <Video size={14} /> {incoming ? t("call.incomingVideo") : t("call.callingLabel")}
        </p>

        <div className="relative mt-10">
          <span className="absolute inset-0 rounded-full bg-[#E60000]/30 animate-ping" style={{ animationDuration: "1.8s" }} />
          <span className="absolute -inset-3 rounded-full border border-[#E60000]/30" />
          <div className="relative w-32 h-32 rounded-full overflow-hidden border-2 border-white/20 shadow-[0_0_40px_rgba(230,0,0,0.4)] bg-black flex items-center justify-center">
            {photo
              ? <img src={photo} alt="" className="w-full h-full object-cover" data-testid="call-avatar" />
              : <span className="text-5xl font-display text-white/80">{(name || "?").slice(0, 1).toUpperCase()}</span>}
          </div>
        </div>

        <h1 className="mt-8 text-3xl font-display tracking-tight text-white" data-testid="call-person-name">{name}</h1>
        <p className="mt-2 text-sm text-zinc-400 font-mono-accent">
          {incoming ? t("call.ringingIn") : t("call.ringingOut")}
        </p>
      </div>

      {/* Controls */}
      <div className="relative w-full max-w-md px-10 pb-[12vh]">
        {incoming ? (
          <div className="flex items-end justify-between">
            <button onClick={onDecline} data-testid="call-decline-btn" className="flex flex-col items-center gap-2 group">
              <span className="w-[72px] h-[72px] rounded-full bg-[#E60000] group-active:scale-90 transition-transform flex items-center justify-center shadow-[0_0_30px_rgba(230,0,0,0.5)]">
                <PhoneOff size={30} className="text-white" />
              </span>
              <span className="text-xs text-zinc-300 font-mono-accent">{t("call.decline")}</span>
            </button>
            <button onClick={onAccept} data-testid="call-accept-btn" className="flex flex-col items-center gap-2 group">
              <span className="w-[72px] h-[72px] rounded-full bg-[#22C55E] group-active:scale-90 transition-transform flex items-center justify-center shadow-[0_0_30px_rgba(34,197,94,0.5)] animate-bounce" style={{ animationDuration: "1.6s" }}>
                <Phone size={30} className="text-white" />
              </span>
              <span className="text-xs text-zinc-300 font-mono-accent">{t("call.accept")}</span>
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <button onClick={onCancel} data-testid="call-cancel-btn" className="flex flex-col items-center gap-2 group">
              <span className="w-[72px] h-[72px] rounded-full bg-[#E60000] group-active:scale-90 transition-transform flex items-center justify-center shadow-[0_0_30px_rgba(230,0,0,0.5)]">
                <PhoneOff size={30} className="text-white" />
              </span>
              <span className="text-xs text-zinc-300 font-mono-accent">{t("call.cancel")}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
